import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RichTextEditor, RichTextView } from "@/components/RichText";
import {
  X, Pencil, Save, Trash2, Check, Plus, Filter, History, CheckSquare, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DatePicker from "@/components/DatePicker";
import {
  useActivities, useCreateActivity, useUpdateActivity,
  useCompleteActivity, useDeleteActivity,
  useNotes, useCreateNote, useUpdateNote, useDeleteNote,
  useUpdateDeal, useDealTasks, useCreateTask, useUpdateTask,
  useToggleTask, useDeleteTask, useDealNextStep, useStages, useActivityStatuses,
} from "@/hooks/useCrmQueries";
import {
  ACTIVITY_TYPES, ACTIVITY_MAP, displayStatus, STATUS_STYLE, dayLabel, fmtDateTime, isOverdue,
} from "@/lib/activities";

const STAGES_FALLBACK = [
  { key: "new_lead", label: "New Lead" },
  { key: "qualified", label: "Qualified" },
  { key: "proposal", label: "Proposal" },
  { key: "negotiation", label: "Negotiation" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

const currency = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function DealDetail({ deal, users, companies, contacts, onClose, onEditDeal, onDeleteDeal }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("timeline");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [suggestedType, setSuggestedType] = useState(null);
  const [openFormNonce, setOpenFormNonce] = useState(0);

  const filters = useMemo(() => {
    const f = {};
    if (filterType !== "all") f.activity_type = filterType;
    if (filterStatus !== "all") f.status = filterStatus;
    if (dateFrom) f.date_from = new Date(dateFrom).toISOString();
    if (dateTo) f.date_to = new Date(dateTo + "T23:59:59").toISOString();
    return f;
  }, [filterType, filterStatus, dateFrom, dateTo]);

  const { data: activities = [] } = useActivities(deal?.id, filters);
  const { data: notes = [] } = useNotes(deal?.id);
  const { data: dealTasks = [] } = useDealTasks(deal?.id);
  const { data: nextStep } = useDealNextStep(deal?.id);
  const { data: stagesData = [] } = useStages();
  const { data: statuses = [] } = useActivityStatuses();
  const STAGES = stagesData.length ? stagesData : STAGES_FALLBACK;

  const createAct = useCreateActivity(deal?.id);
  const updateAct = useUpdateActivity(deal?.id);
  const completeAct = useCompleteActivity(deal?.id);
  const deleteAct = useDeleteActivity(deal?.id);

  const createNote = useCreateNote(deal?.id);
  const updateNote = useUpdateNote(deal?.id);
  const deleteNote = useDeleteNote(deal?.id);

  const updateDeal = useUpdateDeal();

  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const toggleTask = useToggleTask();
  const deleteTask = useDeleteTask();

  const companyMap = Object.fromEntries((companies || []).map((c) => [c.id, c.name]));
  const contactMap = Object.fromEntries((contacts || []).map((c) => [c.id, c.name]));
  const userMap = Object.fromEntries((users || []).map((u) => [u.id, u.name]));

  if (!deal) return null;

  // ---- Upcoming (planned, sorted by scheduled_date ascending) ----
  const upcoming = activities
    .filter((a) => a.status === "planned" && a.scheduled_date)
    .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));

  // ---- Move stage (quick action) ----
  const handleMoveStage = async (stage) => {
    try {
      await updateDeal.mutateAsync({ id: deal.id, payload: { ...dealToPayload(deal), stage } });
      toast.success(`Moved to ${stage}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to move stage");
    }
  };

  return (
    <Sheet open={!!deal} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[1100px] p-0 flex flex-col gap-0 overflow-hidden"
        data-testid="deal-detail"
      >
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b border-border bg-card space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="eyebrow mb-1">Deal · {STAGES.find((s) => s.key === deal.stage)?.label}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <SheetTitle className="text-2xl tracking-tight font-medium truncate" style={{ fontFamily: "Outfit" }}>
                  {deal.title}
                </SheetTitle>
                {deal.deal_type && (
                  <span
                    data-testid="deal-detail-type-chip"
                    className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded"
                    style={
                      deal.deal_type === "k12"
                        ? { background: "hsl(32 94% 44% / 0.14)", color: "hsl(32 94% 34%)" }
                        : deal.deal_type === "hei"
                        ? { background: "hsl(280 55% 45% / 0.14)", color: "hsl(280 55% 40%)" }
                        : { background: "hsl(222 100% 33% / 0.10)", color: "hsl(222 100% 33%)" }
                    }
                  >
                    {deal.deal_type.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {companyMap[deal.company_id] || "—"} · {contactMap[deal.contact_id] || "—"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => onEditDeal(deal)} data-testid="deal-edit-btn">
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="deal-move-stage">Move stage</Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-44 p-1">
                  {STAGES.map((s) => (
                    <button
                      key={s.key}
                      data-testid={`move-stage-${s.key}`}
                      onClick={() => handleMoveStage(s.key)}
                      disabled={s.key === deal.stage}
                      className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-accent transition-colors ${
                        s.key === deal.stage ? "text-muted-foreground" : ""
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </PopoverContent>
              </Popover>
              <Button variant="ghost" size="icon" onClick={onClose} data-testid="deal-detail-close">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Header stats row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-2">
            <HdrStat label="Value" value={currency(deal.value)} />
            <HdrStat label="Stage" value={STAGES.find((s) => s.key === deal.stage)?.label} />
            <HdrStat label="Close date" value={deal.expected_close ? new Date(deal.expected_close).toLocaleDateString() : "—"} />
            <HdrStat label="Owner" value={userMap[deal.owner_id] || "—"} />
            <HdrStat label="Probability" value={deal.probability != null ? `${deal.probability}%` : "—"} />
          </div>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-5 gap-0 overflow-hidden">
          {/* Left column */}
          <div className="lg:col-span-2 border-r border-border overflow-y-auto p-6 space-y-6">
            <section>
              <div className="eyebrow mb-2">Description</div>
              <p className="text-sm whitespace-pre-wrap text-foreground/90 leading-relaxed">
                {deal.description || <span className="text-muted-foreground italic">No description. Click Edit to add one.</span>}
              </p>
            </section>

            <section>
              <div className="eyebrow mb-2">Deal info</div>
              <dl className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                <InfoRow label="Company" value={companyMap[deal.company_id] || "—"} />
                <InfoRow label="Contact" value={contactMap[deal.contact_id] || "—"} />
                <InfoRow label="Owner" value={userMap[deal.owner_id] || "—"} />
                <InfoRow label="Created" value={new Date(deal.created_at).toLocaleDateString()} />
              </dl>
            </section>

            <NotesPanel
              notes={notes}
              me={user}
              userMap={userMap}
              onCreate={(text) => createNote.mutateAsync({ text }).then(() => toast.success("Note added")).catch((e) => toast.error(e.response?.data?.detail || "Failed"))}
              onUpdate={(id, text) => updateNote.mutateAsync({ id, payload: { text } }).then(() => toast.success("Note updated")).catch((e) => toast.error(e.response?.data?.detail || "Failed"))}
              onDelete={(id) => deleteNote.mutateAsync(id).then(() => toast.success("Note deleted")).catch((e) => toast.error(e.response?.data?.detail || "Failed"))}
            />
          </div>

          {/* Right column */}
          <div className="lg:col-span-3 overflow-y-auto p-6 space-y-6">
            {/* Server-suggested next step */}
            {nextStep && (
              <NextStepBanner
                hint={nextStep}
                onLog={() => {
                  setTab("timeline");
                  setSuggestedType(nextStep.suggested_activity_type || "task");
                  setOpenFormNonce((n) => n + 1);
                }}
              />
            )}

            {/* Upcoming */}
            <section data-testid="upcoming-activities">
              <div className="flex items-center justify-between mb-3">
                <div className="eyebrow">Upcoming activities</div>
                <span className="text-xs text-muted-foreground">{upcoming.length} planned</span>
              </div>
              {upcoming.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">Nothing scheduled.</div>
              ) : (
                <ul className="space-y-1.5">
                  {upcoming.slice(0, 5).map((a) => (
                    <UpcomingItem
                      key={a.id}
                      a={a}
                      onComplete={() => completeAct.mutateAsync(a.id).then(() => toast.success("Completed")).catch((e) => toast.error(e.response?.data?.detail || "Failed"))}
                    />
                  ))}
                </ul>
              )}
            </section>

            {/* Tabs */}
            <section>
              <div className="flex items-center gap-1 border-b border-border">
                {[
                  { k: "timeline", label: "Timeline", count: activities.length },
                  { k: "tasks",    label: "Related tasks", count: dealTasks.length },
                ].map(({ k, label, count }) => (
                  <button
                    key={k}
                    data-testid={`deal-tab-${k}`}
                    onClick={() => setTab(k)}
                    className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors duration-150 ${
                      tab === k
                        ? "border-primary text-primary font-medium"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {label} <span className="ml-1.5 text-xs text-muted-foreground">{count}</span>
                  </button>
                ))}
              </div>

              {tab === "timeline" && (
                <TimelineTab
                  deal={deal}
                  activities={activities}
                  userMap={userMap}
                  filterType={filterType} setFilterType={setFilterType}
                  filterStatus={filterStatus} setFilterStatus={setFilterStatus}
                  dateFrom={dateFrom} setDateFrom={setDateFrom}
                  dateTo={dateTo} setDateTo={setDateTo}
                  createAct={createAct}
                  updateAct={updateAct}
                  completeAct={completeAct}
                  deleteAct={deleteAct}
                  users={users}
                  suggestedType={suggestedType}
                  openFormNonce={openFormNonce}
                  clearSuggested={() => setSuggestedType(null)}
                />
              )}
              {tab === "tasks" && (
                <TasksTab
                  deal={deal}
                  tasks={dealTasks}
                  users={users}
                  userMap={userMap}
                  createTask={createTask}
                  updateTask={updateTask}
                  toggleTask={toggleTask}
                  deleteTask={deleteTask}
                />
              )}
            </section>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* -------------------- helpers -------------------- */
function dealToPayload(d) {
  return {
    title: d.title, value: d.value, stage: d.stage,
    contact_id: d.contact_id || null, company_id: d.company_id || null,
    owner_id: d.owner_id || null, expected_close: d.expected_close || null,
    probability: d.probability ?? null, description: d.description || "",
    notes: d.notes || "",
  };
}

function HdrStat({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="text-sm font-medium truncate mt-0.5">{value}</div>
    </div>
  );
}
function InfoRow({ label, value }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium truncate">{value}</dd>
    </>
  );
}

/* -------------------- Next Step banner -------------------- */
const TONE_STYLES = {
  primary: {
    wrap: "border-primary/40 bg-primary/5",
    icon: "text-primary",
    btn:  "bg-primary hover:bg-primary/90 text-primary-foreground",
    label: "text-primary",
  },
  warn: {
    wrap: "border-orange-400/50 bg-orange-500/5",
    icon: "text-orange-600",
    btn:  "bg-orange-600 hover:bg-orange-700 text-white",
    label: "text-orange-700",
  },
  danger: {
    wrap: "border-destructive/40 bg-destructive/5",
    icon: "text-destructive",
    btn:  "bg-destructive hover:bg-destructive/90 text-destructive-foreground",
    label: "text-destructive",
  },
  neutral: {
    wrap: "border-border bg-secondary/40",
    icon: "text-muted-foreground",
    btn:  "bg-secondary text-foreground hover:bg-secondary/80",
    label: "text-muted-foreground",
  },
};

function NextStepBanner({ hint, onLog }) {
  const tone = TONE_STYLES[hint.tone] || TONE_STYLES.primary;
  const SuggestedIcon = ACTIVITY_MAP[hint.suggested_activity_type]?.icon;
  return (
    <div
      data-testid="next-step-banner"
      className={`rounded-md border p-4 flex items-start gap-3 ${tone.wrap}`}
    >
      <Sparkles className={`h-5 w-5 mt-0.5 shrink-0 ${tone.icon}`} strokeWidth={1.75} />
      <div className="flex-1 min-w-0">
        <div className={`text-[10px] uppercase tracking-[0.22em] font-bold mb-1 ${tone.label}`}>
          Ideal next step
        </div>
        <div className="text-sm font-medium">{hint.headline}</div>
        <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{hint.detail}</div>
        {hint.days_since_last_activity != null && (
          <div className="text-[11px] text-muted-foreground mt-1.5 mono">
            {hint.days_since_last_activity === 0
              ? "Last touch: today"
              : `Last touch: ${hint.days_since_last_activity}d ago`}
            {hint.upcoming_count != null && ` · ${hint.upcoming_count} planned`}
          </div>
        )}
      </div>
      {hint.suggested_activity_type && (
        <Button
          size="sm"
          onClick={onLog}
          data-testid="next-step-log"
          className={`${tone.btn} shrink-0`}
        >
          {SuggestedIcon && <SuggestedIcon className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} />}
          Log it
        </Button>
      )}
    </div>
  );
}

/* -------------------- Upcoming item -------------------- */
function UpcomingItem({ a, onComplete }) {
  const Icon = ACTIVITY_MAP[a.activity_type]?.icon || null;
  const overdue = isOverdue(a);
  return (
    <li
      data-testid={`upcoming-${a.id}`}
      className={`flex items-center gap-3 px-3 py-2 rounded border border-border bg-card ${
        overdue ? "border-destructive/40 bg-destructive/5" : ""
      }`}
    >
      {Icon && <Icon className={`h-4 w-4 shrink-0 ${overdue ? "text-destructive" : "text-muted-foreground"}`} strokeWidth={1.75} />}
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{a.title}</div>
        <div className={`text-[11px] ${overdue ? "text-destructive font-medium" : "text-muted-foreground"}`}>
          {overdue && "Overdue · "}
          {dayLabel(a.scheduled_date)} · {fmtDateTime(a.scheduled_date)}
        </div>
      </div>
      <button
        data-testid={`upcoming-complete-${a.id}`}
        onClick={onComplete}
        className="text-muted-foreground hover:text-emerald-700 transition-colors"
        title="Mark complete"
      >
        <Check className="h-4 w-4" />
      </button>
    </li>
  );
}

/* -------------------- Timeline tab -------------------- */
function TimelineTab({
  deal, activities, userMap,
  filterType, setFilterType, filterStatus, setFilterStatus,
  dateFrom, setDateFrom, dateTo, setDateTo,
  createAct, updateAct, completeAct, deleteAct, users,
  suggestedType, openFormNonce, clearSuggested,
}) {
  const { data: statuses = [] } = useActivityStatuses();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // activity or null
  const [presetType, setPresetType] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // React to "Log it" from NextStepBanner: parent bumps openFormNonce whenever
  // a fresh suggestion is available. Setters from useState are stable and
  // don't need to be listed in deps.
  useEffect(() => {
    if (openFormNonce > 0 && suggestedType) {
      setEditing(null);
      setPresetType(suggestedType);
      setShowForm(true);
    }
  }, [openFormNonce, suggestedType]);

  // Ascending by scheduled_date (falling back to created_at for undated items).
  // Undated activities appear at the end.
  const sorted = [...activities].sort((a, b) => {
    const aDated = !!a.scheduled_date;
    const bDated = !!b.scheduled_date;
    if (aDated !== bDated) return aDated ? -1 : 1;
    const ta = new Date(a.scheduled_date || a.created_at).getTime();
    const tb = new Date(b.scheduled_date || b.created_at).getTime();
    return ta - tb;
  });

  return (
    <div className="pt-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          data-testid="add-activity-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add activity
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowFilters((v) => !v)} data-testid="toggle-filters">
          <Filter className="h-3.5 w-3.5 mr-1.5" /> Filters
        </Button>
        {(filterType !== "all" || filterStatus !== "all" || dateFrom || dateTo) && (
          <button
            onClick={() => { setFilterType("all"); setFilterStatus("all"); setDateFrom(""); setDateTo(""); }}
            className="text-xs text-muted-foreground underline"
            data-testid="clear-filters"
          >
            Clear
          </button>
        )}
      </div>

      {showFilters && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 rounded border border-border bg-secondary/40">
          <div>
            <Label className="text-xs">Type</Label>
            <select
              data-testid="filter-type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="h-8 border border-input rounded-md px-2 text-sm bg-background w-full"
            >
              <option value="all">All types</option>
              <option value="calls">Calls</option>
              <option value="meetings">Meetings</option>
              <option value="emails">Emails</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="demos">Demos</option>
              <optgroup label="Specific">
                {ACTIVITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </optgroup>
            </select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <select
              data-testid="filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-8 border border-input rounded-md px-2 text-sm bg-background w-full"
            >
              <option value="all">All</option>
              {(statuses || []).map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input data-testid="filter-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input data-testid="filter-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8" />
          </div>
        </div>
      )}

      {showForm && (
        <ActivityForm
          initial={editing || (presetType ? { activity_type: presetType } : null)}
          users={users}
          onCancel={() => { setShowForm(false); setEditing(null); setPresetType(null); clearSuggested?.(); }}
          onSave={async (payload) => {
            try {
              if (editing) {
                await updateAct.mutateAsync({ id: editing.id, payload });
                toast.success("Activity updated");
              } else {
                await createAct.mutateAsync(payload);
                toast.success("Activity added");
              }
              setShowForm(false); setEditing(null); setPresetType(null); clearSuggested?.();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Failed");
            }
          }}
        />
      )}

      {sorted.length === 0 ? (
        <div className="text-sm text-muted-foreground italic py-6 text-center border border-dashed border-border rounded-md">
          No activities yet. Log the first one.
        </div>
      ) : (
        <ol className="relative pl-6 space-y-3 border-l border-border ml-2" data-testid="timeline">
          {sorted.map((a) => (
            <TimelineItem
              key={a.id}
              a={a}
              userMap={userMap}
              onEdit={() => { setEditing(a); setShowForm(true); }}
              onComplete={() => completeAct.mutateAsync(a.id).then(() => toast.success("Completed"))}
              onDelete={() => {
                if (!confirm("Delete this activity?")) return;
                deleteAct.mutateAsync(a.id).then(() => toast.success("Deleted"));
              }}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

function TimelineItem({ a, userMap, onEdit, onComplete, onDelete }) {
  const Icon = ACTIVITY_MAP[a.activity_type]?.icon || null;
  const label = ACTIVITY_MAP[a.activity_type]?.label || a.activity_type;
  const status = displayStatus(a);
  return (
    <li className="relative" data-testid={`timeline-item-${a.id}`}>
      <span
        className="absolute -left-[27px] top-1 h-5 w-5 rounded-full bg-card border border-border flex items-center justify-center"
      >
        {Icon && <Icon className="h-3 w-3 text-primary" strokeWidth={1.75} />}
      </span>
      <div className="rounded-md border border-border bg-card p-3 group">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_STYLE[status]}`}>
                {status}
              </span>
            </div>
            <div className="text-sm font-medium mt-0.5">{a.title}</div>
            {a.description && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{a.description}</p>}
            <div className="text-[11px] text-muted-foreground mt-2 flex gap-3 flex-wrap">
              {a.scheduled_date && <span>Scheduled: {fmtDateTime(a.scheduled_date)}</span>}
              {a.completed_date && <span>Completed: {fmtDateTime(a.completed_date)}</span>}
              <span>By: {userMap[a.assignee_id] || userMap[a.created_by] || "—"}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {a.status !== "completed" && (
              <button
                data-testid={`complete-activity-${a.id}`}
                onClick={onComplete}
                className="p-1 text-muted-foreground hover:text-emerald-700"
                title="Mark complete"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              data-testid={`edit-activity-${a.id}`}
              onClick={onEdit}
              className="p-1 text-muted-foreground hover:text-primary"
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid={`delete-activity-${a.id}`}
              onClick={onDelete}
              className="p-1 text-muted-foreground hover:text-destructive"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}

/* -------------------- Activity form -------------------- */
function ActivityForm({ initial, users, statuses = [], onCancel, onSave }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    activity_type: initial?.activity_type || "phone_call",
    description: initial?.description || "",
    scheduled_date: initial?.scheduled_date || "",
    assignee_id: initial?.assignee_id || "",
    status: initial?.status || "planned",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave({
      title: form.title,
      activity_type: form.activity_type,
      description: form.description,
      scheduled_date: form.scheduled_date || null,
      assignee_id: form.assignee_id || null,
      status: form.status,
    });
    setSaving(false);
  };
  const statusOptions = statuses.length ? statuses : [
    { key: "planned",   label: "Planned"   },
    { key: "completed", label: "Completed" },
    { key: "cancelled", label: "Cancelled" },
  ];
  return (
    <form
      onSubmit={submit}
      data-testid="activity-form"
      className="p-4 rounded-md border border-border bg-card space-y-3"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2 space-y-1.5">
          <Label>Title</Label>
          <Input
            data-testid="activity-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label>Type</Label>
          <select
            data-testid="activity-type"
            className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
            value={form.activity_type}
            onChange={(e) => setForm({ ...form, activity_type: e.target.value })}
          >
            {ACTIVITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <Textarea
          data-testid="activity-description"
          rows={3}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label>Scheduled</Label>
          <DatePicker
            testId="activity-scheduled"
            value={form.scheduled_date}
            onChange={(v) => setForm({ ...form, scheduled_date: v || "" })}
            withTime
            placeholder="Pick date & time"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Assigned to</Label>
          <select
            data-testid="activity-assignee"
            className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
            value={form.assignee_id}
            onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}
          >
            <option value="">— Me —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Status</Label>
          <select
            data-testid="activity-status"
            className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            {statusOptions.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} data-testid="activity-cancel">Cancel</Button>
        <Button type="submit" size="sm" disabled={saving || !form.title} data-testid="activity-save" className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Save className="h-3.5 w-3.5 mr-1.5" />
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

/* -------------------- Notes -------------------- */
function NotesPanel({ notes, me, userMap, onCreate, onUpdate, onDelete }) {
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const canEditNote = (n) => me?.role === "admin" || n.author_id === me?.id;

  return (
    <section data-testid="notes-panel">
      <div className="eyebrow mb-2">Notes</div>
      <div className="rounded-md border border-border bg-card p-3 space-y-2">
        <RichTextEditor
          value={text}
          onChange={setText}
          placeholder="Log a note — use the toolbar for bold, italics and lists…"
          testid="note-input"
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            disabled={!text.trim()}
            data-testid="note-save"
            onClick={async () => { await onCreate(text); setText(""); }}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Add note
          </Button>
        </div>
      </div>

      <ul className="mt-3 space-y-2" data-testid="notes-list">
        {notes.length === 0 && <li className="text-xs text-muted-foreground italic">No notes yet.</li>}
        {notes.map((n) => (
          <li key={n.id} className="rounded-md border border-border bg-card p-3" data-testid={`note-${n.id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs text-muted-foreground">
                {userMap[n.author_id] || "Someone"} · {fmtDateTime(n.created_at)}
                {n.versions?.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="ml-2 inline-flex items-center gap-1 text-primary hover:underline" data-testid={`note-history-${n.id}`}>
                        <History className="h-3 w-3" /> {n.versions.length} edit{n.versions.length > 1 ? "s" : ""}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-80 max-h-64 overflow-y-auto text-xs space-y-2">
                      {n.versions.slice().reverse().map((v) => (
                        <div key={`${n.id}-${v.edited_at}`} className="border-b border-border pb-2 last:border-0">
                          <div className="text-muted-foreground mb-1">{fmtDateTime(v.edited_at)}</div>
                          <RichTextView content={v.text} />
                        </div>
                      ))}
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              {canEditNote(n) && (
                <div className="flex gap-1">
                  {editingId === n.id ? null : (
                    <button
                      onClick={() => { setEditingId(n.id); setEditingText(n.text); }}
                      className="text-muted-foreground hover:text-primary"
                      data-testid={`edit-note-${n.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => confirm("Delete this note?") && onDelete(n.id)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`delete-note-${n.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
            {editingId === n.id ? (
              <div className="space-y-2">
                <RichTextEditor
                  value={editingText}
                  onChange={setEditingText}
                  placeholder="Edit note…"
                  testid={`edit-note-input-${n.id}`}
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                  <Button
                    size="sm"
                    data-testid={`save-note-${n.id}`}
                    onClick={async () => { await onUpdate(n.id, editingText); setEditingId(null); }}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <RichTextView content={n.text} />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* -------------------- Related tasks -------------------- */
function TasksTab({ deal, tasks, users, userMap, createTask, updateTask, toggleTask, deleteTask }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  return (
    <div className="pt-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{tasks.length} task{tasks.length !== 1 ? "s" : ""} linked to this deal</div>
        <Button
          size="sm"
          data-testid="add-related-task-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add task
        </Button>
      </div>

      {showForm && (
        <TaskInlineForm
          initial={editing}
          users={users}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSave={async (payload) => {
            try {
              const withDeal = { ...payload, related_deal_id: deal.id };
              if (editing) {
                await updateTask.mutateAsync({ id: editing.id, payload: withDeal });
                toast.success("Task updated");
              } else {
                await createTask.mutateAsync(withDeal);
                toast.success("Task added");
              }
              setShowForm(false); setEditing(null);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Failed");
            }
          }}
        />
      )}

      <ul className="rounded-md border border-border divide-y divide-border bg-card">
        {tasks.length === 0 && <li className="p-4 text-sm text-muted-foreground italic text-center">No related tasks.</li>}
        {tasks.map((t) => (
          <li key={t.id} className="p-3 flex items-center gap-3" data-testid={`related-task-${t.id}`}>
            <input
              type="checkbox"
              data-testid={`related-task-toggle-${t.id}`}
              checked={t.status === "completed"}
              onChange={() => toggleTask.mutateAsync(t.id)}
              className="h-4 w-4 rounded border-input"
            />
            <div className={`flex-1 text-sm ${t.status === "completed" ? "line-through text-muted-foreground" : ""}`}>
              {t.title}
              <div className="text-[11px] text-muted-foreground">
                {t.type} · {t.priority} · {t.status?.replace("_", " ") || "not started"} · {userMap[t.assignee_id] || "—"}
                {t.start_date && ` · start ${new Date(t.start_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`}
                {t.due_date && ` · due ${new Date(t.due_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`}
              </div>
            </div>
            <button onClick={() => { setEditing(t); setShowForm(true); }} className="text-muted-foreground hover:text-primary" data-testid={`edit-related-task-${t.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => confirm("Delete this task?") && deleteTask.mutateAsync({ id: t.id, related_deal_id: deal.id }).then(() => toast.success("Removed"))}
              className="text-muted-foreground hover:text-destructive"
              data-testid={`delete-related-task-${t.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TaskInlineForm({ initial, users, onCancel, onSave }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    type: initial?.type || "task",
    priority: initial?.priority || "medium",
    status: initial?.status || "not_started",
    start_date: initial?.start_date || "",
    due_date: initial?.due_date || "",
    assignee_id: initial?.assignee_id || "",
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          ...form,
          assignee_id: form.assignee_id || null,
          start_date: form.start_date || null,
          due_date: form.due_date || null,
        });
      }}
      className="p-3 rounded-md border border-border bg-card space-y-2"
      data-testid="related-task-form"
    >
      <Input
        data-testid="related-task-title"
        placeholder="Task title"
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        required
      />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <select className="h-9 border border-input rounded-md px-2 text-sm bg-background" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
          <option value="task">Task</option><option value="call">Call</option>
          <option value="email">Email</option><option value="meeting">Meeting</option><option value="note">Note</option>
        </select>
        <select className="h-9 border border-input rounded-md px-2 text-sm bg-background" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
          <option value="urgent">Urgent</option>
          <option value="important">Important</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select className="h-9 border border-input rounded-md px-2 text-sm bg-background" value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}>
          <option value="">— Me —</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <div className="text-[11px] text-muted-foreground mb-1">Start</div>
          <DatePicker
            testId="related-task-start"
            value={form.start_date}
            onChange={(v) => setForm({ ...form, start_date: v || "" })}
            placeholder="Start date"
          />
        </div>
        <div>
          <div className="text-[11px] text-muted-foreground mb-1">Due</div>
          <DatePicker
            testId="related-task-due"
            value={form.due_date}
            onChange={(v) => setForm({ ...form, due_date: v || "" })}
            placeholder="Due date"
          />
        </div>
        <div>
          <div className="text-[11px] text-muted-foreground mb-1">Status</div>
          <select className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="not_started">Not Started</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
        <Button type="submit" size="sm" data-testid="related-task-save" className="bg-primary text-primary-foreground hover:bg-primary/90">Save</Button>
      </div>
    </form>
  );
}
